import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Plugs, Plus, X, PencilSimple, Check } from '@phosphor-icons/react';
import { CopyApiButton } from '../components/CopyApiButton';

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [form, setForm] = useState({ label: '', do_token: '', access_key: '', secret_key: '' });

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`/api/accounts`, { withCredentials: true });
      setAccounts(res.data);
    } catch (e) {
      toast.error("Failed to fetch accounts");
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/accounts`, form, { withCredentials: true });
      toast.success("Account added successfully");
      setShowAdd(false);
      setForm({ label: '', do_token: '', access_key: '', secret_key: '' });
      fetchAccounts();
    } catch (e) {
      toast.error("Failed to add account");
    }
  };

  const toggleStatus = async (id) => {
    try {
      await axios.post(`/api/accounts/${id}/toggle`, {}, { withCredentials: true });
      fetchAccounts();
      toast.success("Status updated");
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const removeAccount = async (id) => {
    if (!window.confirm("Are you sure you want to remove this account?")) return;
    try {
      await axios.delete(`/api/accounts/${id}`, { withCredentials: true });
      fetchAccounts();
      toast.success("Account removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove account");
    }
  };

  const testConnection = async (id) => {
    try {
      await axios.post(`/api/accounts/${id}/test`, {}, { withCredentials: true });
      toast.success("Connection OK");
    } catch (e) {
      toast.error("Connection Failed: Invalid Token");
    }
  };

  const saveRename = async (id) => {
    const label = editLabel.trim();
    if (!label) return toast.error("Nickname cannot be empty");
    try {
      await axios.patch(`/api/accounts/${id}`, { label }, { withCredentials: true });
      setEditingId(null);
      fetchAccounts();
      toast.success("Nickname updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename");
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-black">DO ACCOUNTS</h1>
        <button 
          onClick={() => setShowAdd(true)}
          data-testid="add-account-btn"
          className="flex items-center gap-2 bg-primary text-black px-4 py-2 font-bold hover:bg-[#00B8CC] transition-colors"
        >
          <Plus size={20} /> Add Account
        </button>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/20 p-8 w-full max-w-lg relative animate-in zoom-in-95 duration-200">
            <button onClick={() => setShowAdd(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold mb-6">LINK NEW ACCOUNT</h2>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Label / Nickname</label>
                <input required data-testid="input-label" value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">DigitalOcean API Token (PAT)</label>
                <input required type="password" data-testid="input-do-token" value={form.do_token} onChange={(e) => setForm({...form, do_token: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Spaces Access Key</label>
                <input required data-testid="input-access-key" value={form.access_key} onChange={(e) => setForm({...form, access_key: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Spaces Secret Key</label>
                <input required type="password" data-testid="input-secret-key" value={form.secret_key} onChange={(e) => setForm({...form, secret_key: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-primary" />
              </div>
              <button type="submit" data-testid="submit-account-btn" className="w-full bg-primary text-black font-bold py-3 mt-4 hover:bg-[#00B8CC] transition-colors">
                LINK ACCOUNT
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {accounts.map(acc => (
          <div key={acc.id} className="border border-white/10 bg-card p-6 relative group flex flex-col gap-4">
            <div className="flex justify-between items-start">
              <div className="flex flex-col gap-1">
                {editingId === acc.id ? (
                  <div className="flex items-center gap-2">
                    <input autoFocus data-testid={`rename-input-${acc.id}`} value={editLabel} onChange={e => setEditLabel(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveRename(acc.id); if (e.key === 'Escape') setEditingId(null); }}
                      className="bg-[#0A0B0E] border border-primary/50 px-2 py-1 text-lg font-bold focus:outline-none focus:border-primary w-40" />
                    <button onClick={() => saveRename(acc.id)} data-testid={`rename-save-${acc.id}`} className="text-[#39FF14] hover:text-white p-1"><Check size={18} /></button>
                    <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-white p-1"><X size={18} /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold">{acc.label}</h3>
                    <button onClick={() => { setEditingId(acc.id); setEditLabel(acc.label); }} data-testid={`rename-btn-${acc.id}`} className="text-slate-500 hover:text-primary p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Edit nickname"><PencilSimple size={16} /></button>
                  </div>
                )}
                {acc.full_datacenters?.length > 0 && (
                  <span className="text-yellow-500 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 border border-yellow-500/20 bg-yellow-500/10 px-2 py-0.5 w-fit mt-2">
                    {acc.full_datacenters.length} Datacenter(s) Hit DO Bucket Limit
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => testConnection(acc.id)} data-testid={`test-btn-${acc.id}`} className="text-slate-400 hover:text-primary transition-colors p-1" title="Test Connection"><Plugs size={20} /></button>
                <CopyApiButton provider="do" accountId={acc.id} />
                <button onClick={() => removeAccount(acc.id)} data-testid={`remove-btn-${acc.id}`} className="text-slate-400 hover:text-red-500 transition-colors p-1" title="Remove Account"><Trash size={20} /></button>
              </div>
            </div>
            <div className="space-y-2 text-sm font-mono text-slate-400">
              <p>DO Token: <span className="text-white">{acc.do_token}</span></p>
              <p>Access Key: <span className="text-white">{acc.access_key}</span></p>
            </div>
            <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/10">
              <span className={`text-xs font-bold uppercase tracking-wider ${acc.status === 'active' ? 'text-[#39FF14]' : 'text-slate-500'}`}>
                {acc.status}
              </span>
              <button 
                onClick={() => toggleStatus(acc.id)}
                data-testid={`toggle-btn-${acc.id}`}
                className="text-xs font-bold uppercase border border-white/20 px-3 py-1 hover:bg-white hover:text-black transition-colors"
              >
                {acc.status === 'active' ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Accounts;